<template>
  <tile>
    <div>
      <div class="header">
        <p class="title">Your Current Request for Funds</p>
      </div>
      <div class="applicant-info">
        <loader v-if="gettingData" />
        <div v-if="(loanSvcError || dataRetrievalError) && !gettingData">
          <error />
        </div>
        <CurrentRequestForFunds
          v-if="!dataRetrievalError"
          :loading="gettingData || updatingApp"
          @openSchoolConfirmationModal="openSchoolConfirmationModal"
          @openBorrowAmountModal="openBorrowAmountModal"
          @openLoanOptionsModal="openLoanOptionsModal"
        />
        <div class="modals">
          <LocLoanConfigurationModal
            ref="LocLoanConfigurationModalRef"
            v-if="showLoanConfigurationModal"
            :application="application"
            :applicant="applicant"
            :term="application?.schoolConfiguration?.academicTerm || ''"
            :initialDrawRequest="loanConfigurationModule?.applicationInfo?.initialDraw"
            @close="closeLoanConfigModal"
            @complete="handleLoanConfigurationComplete"
            @updateBorrowAmount="handleUpdateBorrowAmount"
          />
          <ConfirmYourLoanDetailsModal
            v-if="confirmYourLoanDetailsModalActive"
            @openSchoolConfirmationModal="openSchoolConfirmationFromConfirm"
            @openBorrowAmountModal="openBorrowAmountFromConfirm"
            @openLoanOptionsModal="openLoanOptionsFromConfirm"
            @openLoanConfigurationModal="openLoanConfigurationFromConfirm"
          />
          <div class="request-funds">
            <next-button
              v-if="
                showRequestFundsButton &&
                !gettingData &&
                !loanSvcError &&
                loan?.availableToBorrow > 0
              "
              @click="showFundsRequestModal = true"
              :text="fundsRequestBtnText"
              :disabled="!loan?.canRequestFund"
            />
            <div
              v-if="showPendingDisbursementNotice(loan)"
            >
              You have a disbursement pending. Once your disbursement is loaded to your loan
              servicer, we will recalculate your amount available to request.
            </div>
            <request-funds-modal-opal
              v-if="showFundsRequestModal"
              :loan="loan"
              @close="showFundsRequestModal = false"
              @getLoanDetails="getLoanDetails"
            />
          </div>
          <borrow-amount
            id="borrowAmount"
            v-if="showBorrowAmountModal"
            :application="application"
            :term="application?.schoolConfiguration?.academicTerm || ''"
            :initialDrawRequest="loanConfigurationModule?.applicationInfo?.initialDraw"
            :loading="gettingData || updatingApp"
            @complete="complete"
            @close="closeBorrowAmountModal"
          />
          <school-confirmation
            id="schoolConfirmation"
            v-if="showSchoolConfirmationModal"
            :application="application"
            :term="application?.schoolConfiguration?.academicTerm || ''"
            :applicant="applicant"
            :loading="gettingData || updatingApp"
            @complete="complete"
            @close="closeSchoolConfirmationModal"
          />
          <loan-options
            id="loanOptions"
            v-if="showLoanOptionsModal && !isValidProfessionalGradFlow"
            :application="application"
            :applicant="applicant"
            :initialDrawAmount="initialDrawRequest?.amount"
            :loading="gettingData || updatingApp"
            @complete="complete"
            @close="closeLoanOptionsModal"
          />
          <PgLoanOptionsModal
            v-if="showLoanOptionsModal && isValidProfessionalGradFlow && hasRepaymentBasedRates"
            @close="closeLoanOptionsModal"
            @configurationComplete="complete($event)"
          />
          <PelLoanOptionsModal
            v-if="showLoanOptionsModal && isValidProfessionalGradFlow && !hasRepaymentBasedRates"
            @close="closeLoanOptionsModal"
            @complete="complete"
          />
          <div v-if="error === true" class="error">
            <timeout-modal></timeout-modal>
          </div>
          <opal-modal />
        </div>
      </div>
    </div>
  </tile>
  <StudentInformation
    v-if="isParentLOC && !gettingData"
    :loading="gettingData"
    :disabled="false"
    :student="student"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import Tile from "@/components/shared/Tile.vue";
import SchoolConfirmation from "@/components/loan-configuration/SchoolConfirmation.vue";
import BorrowAmount from "@/components/loan-configuration/BorrowAmount.vue";
import LoanOptions from "@/components/loan-configuration/LoanOptions.vue";
import TimeoutModal from "@/components/modals/TimeoutModal.vue";
import RequestFundsModalOpal from "@/components/modals/RequestFundsModalOpal.vue";
import NextButton from "@/components/shared/NextButton.vue";
import Error from "@/components/shared/Error.vue";
import OpalModal from "@/components/shared/OpalModal.vue";
import ConfirmYourLoanDetailsModal from "@/components/pel-loan-configuration/ConfirmYourLoanDetailsModal.vue";
import LocLoanConfigurationModal from "@/components/loan-configuration/LocLoanConfigurationModal.vue";
import PelLoanOptionsModal from "@/components/pel-loan-configuration/PelLoanOptionsModal.vue";
import PgLoanOptionsModal from "@/components/professional-grad/PgLoanOptionsModal.vue";
import CurrentRequestForFunds from "@/components/application-info/LOC/CurrentRequestForFunds.vue";
import useApplicationInfo from "@/composables/application-info/useApplicationInfo";
import Loader from "@/components/shared/Loader.vue";
import StudentInformation from "@/components/application-info/parent-loc/student/StudentInformation.vue";
const LocLoanConfigurationModalRef = ref<InstanceType<typeof LocLoanConfigurationModal>>();

defineProps<{
  application: any;
  applicant: any;
}>();

const store = useStore();

const loanConfigurationModule = computed(() => store.state.loanConfigurationModule);
const applicationInfoModule = computed(() => store.state.applicationInfoModule);

const {
  confirmYourLoanDetailsModalActive,
  closeBorrowAmountModal,
  closeLoanConfigModal,
  closeLoanOptionsModal,
  closeSchoolConfirmationModal,
  complete,
  dataRetrievalError,
  error,
  fundsRequestBtnText,
  getApplicationDetails,
  gettingData,
  getLoanDetails,
  handleBorrowAmount,
  handleLoanConfigurationComplete,
  hasRepaymentBasedRates,
  initialDrawRequest,
  isParentLOC,
  isValidProfessionalGradFlow,
  loan,
  loanSvcError,
  openBorrowAmountModal,
  openBorrowAmountFromConfirm,
  openLoanOptionsModal,
  openSchoolConfirmationModal,
  openSchoolConfirmationFromConfirm,
  openLoanOptionsFromConfirm,
  openLoanConfigurationFromConfirm,
  showBorrowAmountModal,
  showFundsRequestModal,
  showLoanConfigurationModal,
  showLoanOptionsModal,
  showRequestFundsButton,
  showSchoolConfirmationModal,
  student,
  updatingApp,
  showPendingDisbursementNotice
} = useApplicationInfo();

async function handleUpdateBorrowAmount(event: {
  amount: number;
  requestedAmountChanged: boolean;
}) {
  const response = await handleBorrowAmount(event);

  if (LocLoanConfigurationModalRef.value) {
    LocLoanConfigurationModalRef.value.updatingBorrowAmount = !response;
  }
}

onMounted(async () => {
  await getApplicationDetails();
});
</script>

<style scoped src="@/styles/application-info/info-tile.css"></style>
<style lang="scss" scoped>
.header {
  margin-bottom: 1rem;

  p {
    margin-bottom: 0;
  }

  .title {
    font-weight: 700;
    font-size: 1.25rem;
  }
}

.balance {
  font-weight: 700;
  font-size: 2em;
  padding: 0;
  margin: 0.5rem 0;

  .cents {
    font-size: 0.6em;
  }

  a.edit-loan-config-link {
    color: #767676;
    font-size: 13px;
    font-weight: 400;
    margin: 0;
    text-align: end;
    text-decoration: underline;
  }
}

.modals {
  .form {
  }

  .error {
    color: red;
    text-align: center;
  }
}

.hide {
  display: none;
}
</style>
